import { useMsal } from "@azure/msal-react";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getAPI } from "src/utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const REPORTS_PATH = `${BACKEND_URL}/v1/system/reports`;

/** Admin users report: `<yyyy-mm-dd>_<instance>_<env>_users.xlsx`. */
export const USERS_REPORT_URL = `${REPORTS_PATH}/users`;

export interface UsageReportPeriod {
    startDate: Date;
    endDate: Date;
}

/** Calendar day as YYYY-MM-DD (the backend reads it as a whole UTC day). */
export const toDay = (date: Date): string => dayjs(date).format("YYYY-MM-DD");

/** Admin usage report for a period (both days inclusive). */
export const buildUsageReportUrl = ({ startDate, endDate }: UsageReportPeriod): string => {
    const params = new URLSearchParams({ start_date: toDay(startDate), end_date: toDay(endDate) });
    return `${REPORTS_PATH}/usage?${params.toString()}`;
};

/** File name from a `Content-Disposition` header (RFC 5987 `filename*` wins), or null. */
export const fileNameFromContentDisposition = (header: string | null): string | null => {
    if (!header) return null;

    const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim());
        } catch {
            // fall through to the plain filename parameter
        }
    }

    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    return plain ? plain[1].trim() : null;
};

const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

export interface ReportDownload {
    url: string;
    /** Used only when the response has no readable `Content-Disposition`. */
    fallbackFileName: string;
}

/**
 * Downloads an admin Excel report (`GET /v1/system/reports/*`) and resolves
 * with the saved file name, taken from the backend's `Content-Disposition`.
 */
export const useReportExport = () => {
    const { instance } = useMsal();

    return useMutation<string, Error, ReportDownload>({
        mutationFn: async ({ url, fallbackFileName }) => {
            const response = await getAPI({ url, instance });
            const fileName =
                fileNameFromContentDisposition(response.headers.get("Content-Disposition")) ??
                fallbackFileName;
            saveBlob(await response.blob(), fileName);
            return fileName;
        },
    });
};
